import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import LiveAdminPanel from "@/components/LiveAdminPanel";
import { CATEGORIES, SLOTS, AdminLogin, ChangePassword, IconBtn } from "@/components/admin/shared";
import ArticlesPanel from "@/components/admin/ArticlesPanel";
import RacesPanel from "@/components/admin/RacesPanel";
import SponsorsPanel from "@/components/admin/SponsorsPanel";
import { Loader2, LogOut, Newspaper, Flag, Megaphone, Mail, CreditCard, Inbox, Trash2 } from "lucide-react";

export default function Admin() {
  const { admin, loading, logout } = useAuth();
  const [articles, setArticles] = useState([]);
  const [races, setRaces] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [eventSubs, setEventSubs] = useState([]);
  const [autoCats, setAutoCats] = useState([]);
  const [genLoading, setGenLoading] = useState(false);

  const AUTO_OPTIONS = [
    { key: "locali", label: "Gare/eventi locali (Rimini · Forlì-Cesena)" },
    { key: "wrc", label: "Rally Europa (WRC/ERC)" },
    { key: "f1", label: "Formula 1 (Kimi Antonelli)" },
    { key: "motogp", label: "MotoGP" },
    { key: "moto2", label: "Moto2" },
  ];

  const loadArticles = () => client.get("/admin/articles").then((r) => setArticles(r.data)).catch((e) => console.error("Load articles failed:", e));
  const load = () => {
    loadArticles();
    client.get("/races").then((r) => setRaces(r.data));
    client.get("/sponsors").then((r) => setSponsors(r.data));
    client.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    client.get("/admin/subscribers").then((r) => setSubs(r.data)).catch(() => {});
    client.get("/admin/event-submissions").then((r) => setEventSubs(r.data)).catch(() => {});
    client.get("/admin/settings").then((r) => setAutoCats(r.data.categories || [])).catch(() => {});
  };

  useEffect(() => { if (admin) load(); }, [admin]); // eslint-disable-line

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-legend-red" /></div>;
  if (!admin) return <AdminLogin />;

  const del = async (path, id, setter, list) => {
    if (!window.confirm("Eliminare definitivamente?")) return;
    await client.delete(`${path}/${id}`);
    setter(list.filter((x) => x.id !== id));
    toast.success("Eliminato.");
  };

  const articleFields = [
    { key: "title", label: "Titolo (IT)", required: true },
    { key: "subtitle", label: "Sottotitolo (IT)" },
    { key: "category", label: "Categoria", type: "select", options: CATEGORIES },
    { key: "excerpt", label: "Estratto (IT)", type: "textarea", rows: 2 },
    { key: "body", label: "Corpo articolo IT (usa ## per titoli, - per elenchi, **grassetto**)", type: "textarea", rows: 8 },
    { key: "ai_translate", type: "ai-translate" },
    { key: "title_en", label: "Title (English)", placeholder: "Traduzione inglese del titolo" },
    { key: "subtitle_en", label: "Subtitle (English)", type: "textarea", rows: 2 },
    { key: "excerpt_en", label: "Excerpt (English)", type: "textarea", rows: 2 },
    { key: "body_en", label: "Body (English)", type: "textarea", rows: 8 },
    { key: "cover_image", label: "Immagine di copertina", type: "image" },
    { key: "sec_media", label: "Foto, video e social", type: "section" },
    { key: "gallery", label: "Galleria foto", type: "gallery" },
    { key: "video_url", label: "Video: link YouTube o reel Instagram (viene convertito in automatico)", placeholder: "https://www.youtube.com/watch?v=... oppure https://www.instagram.com/reel/..." },
    { key: "instagram_url", label: "Link post/profilo Instagram (pulsante social)", placeholder: "https://www.instagram.com/..." },
    { key: "sec_pub", label: "Pubblicazione e collegamenti", type: "section" },
    { key: "related_slugs", label: "Articoli correlati (max 3)", type: "related", options: articles.map((x) => ({ value: x.slug, label: x.title })) },
    { key: "author", label: "Autore" },
    { key: "read_time", label: "Minuti di lettura", type: "number" },
    { key: "issue", label: "Numero (es. Ottobre 2026)" },
    { key: "published_at", label: "Data pubblicazione (AAAA-MM-GG)", placeholder: "2026-09-25" },
    { key: "featured", label: "In prima pagina (hero) — sostituisce l'articolo in evidenza attuale", type: "checkbox" },
    { key: "world", label: "Mostra in \"Dal mondo motorsport\"", type: "checkbox" },
    { key: "world_featured", label: "Apertura di \"Dal mondo motorsport\" (se nessuno è scelto, va il più recente)", type: "checkbox" },
    { key: "published", label: "Pubblicato (togli la spunta per salvarlo come bozza)", type: "checkbox" },
  ];
  const raceFields = [
    { key: "name", label: "Nome gara (IT)", required: true },
    { key: "name_en", label: "Race name (EN)" },
    { key: "location", label: "Località", required: true },
    { key: "date", label: "Data (AAAA-MM-GG)", required: true, placeholder: "2026-10-18" },
    { key: "date_label", label: "Etichetta data (es. 18 Ottobre 2026)" },
    { key: "category", label: "Categoria", type: "select", options: ["Slalom", "Rally", "Ronde", "Historic", "Storiche", "WRC", "ERC", "F1", "MotoGP", "Moto2", "WorldSBK", "Formula E", "Drift", "MTB", "Bike Park", "MTB Area", "Raduno MTB", "Sentiero E.S.P.", "Enduro", "Trial", "EnduroGP", "TrialGP", "Truck", "Trattori", "Pista", "CIGT Sprint", "CIGT Endurance", "GT4 Italy", "Fiera", "Time Attack", "Raduno", "Apecar", "CIR", "CIAR", "CIRT", "Evento"] },
    { key: "discipline", label: "Disciplina (per i filtri)", type: "select", options: ["Rally", "Storiche", "Drift", "MTB", "Enduro", "Truck", "Trattori", "Pista", "Fiere", "F1", "MotoGP", "Moto2", "Superbike", "Formula E", "Apecar", "Raduno", "Evento"] },
    { key: "status", label: "Stato", type: "select", options: ["In programma", "Concluso", "Rinviato", "Annullato", "Stagione 2026", "Area permanente", "Percorribile tutto l'anno"] },
    { key: "region", label: "Area", type: "select", options: ["Italia", "Europa"] },
    { key: "championship", label: "Campionato / Serie" },
    { key: "official_url", label: "Sito ufficiale (URL)", placeholder: "https://" },
    { key: "tickets_url", label: "Biglietti / Prezzi (URL)", placeholder: "https://" },
    { key: "live_url", label: "Diretta (URL Instagram)", placeholder: "https://" },
    { key: "logo", label: "URL logo evento (facoltativo)" },
    { key: "poster", label: "Locandina evento (carica immagine)", type: "image" },
    { key: "organizer", label: "Organizzatore" },
    { key: "organizer_url", label: "Link organizzatore (URL)" },
    { key: "orari_text", label: "Orari (uno per riga)", type: "textarea", rows: 6 },
    { key: "registration_deadline", label: "Scadenza iscrizioni (AAAA-MM-GG)", placeholder: "2026-10-11" },
    { key: "sec_map", label: "Mappa e collegamenti", type: "section" },
    { key: "lat", label: "Latitudine (posizione sulla mappa)", type: "number", placeholder: "43.98" },
    { key: "lng", label: "Longitudine (posizione sulla mappa)", type: "number", placeholder: "12.42" },
    { key: "article_slug", label: "Articolo collegato", type: "select", allowEmpty: true, options: articles.map((x) => ({ value: x.slug, label: x.title })) },
    { key: "komoot_url", label: "Link percorso (Komoot / traccia)", placeholder: "https://www.komoot.com/..." },
  ];
  const sponsorFields = [
    { key: "name", label: "Nome inserzionista", required: true },
    { key: "logo", label: "Logo (carica immagine)", type: "image" },
    { key: "url", label: "Link (URL)", placeholder: "https://" },
    { key: "slot", label: "Posizione", type: "select", options: SLOTS },
    { key: "tagline", label: "Tagline" },
    { key: "order", label: "Ordine (0 = primo)", type: "number" },
  ];

  const saveArticle = async (f) => {
    const payload = { ...f, read_time: Number(f.read_time) || 4, gallery: (f.gallery || []).filter((g) => g.url) };
    if (f.id) { await client.put(`/admin/articles/${f.id}`, payload); }
    else { await client.post("/admin/articles", payload); }
    loadArticles();
  };
  const togglePublish = async (a) => {
    await client.put(`/admin/articles/${a.id}`, { ...a, published: !(a.published !== false) });
    loadArticles();
    toast.success(a.published !== false ? "Spostato in bozze." : "Pubblicato!");
  };
  const postToIg = async (a) => {
    try {
      await client.post(`/admin/articles/${a.id}/instagram`);
      toast.success("Pubblicato su Instagram!");
      loadArticles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Instagram non configurato o errore di pubblicazione.");
    }
  };
  const igKitManual = async (a) => {
    const caption = `${a.title}\n\n${a.excerpt || ""}\n\nLeggi l'articolo completo su legendmagazine.it\n\n#LegendMagazine #Motorsport #Rally #Romagna`;
    try { await navigator.clipboard.writeText(caption); } catch (e) { /* clipboard may be blocked */ }
    try {
      const r = await fetch(a.cover_image);
      const b = await r.blob();
      const u = URL.createObjectURL(b);
      const el = document.createElement("a");
      el.href = u; el.download = `${a.slug || "legend-post"}.jpg`;
      document.body.appendChild(el); el.click(); el.remove();
      URL.revokeObjectURL(u);
    } catch (e) { window.open(a.cover_image, "_blank"); }
    window.open("https://www.instagram.com/", "_blank");
    toast.success("Didascalia copiata e immagine scaricata! Ora incollala nel nuovo post Instagram.");
  };
  const generateDrafts = async () => {
    setGenLoading(true);
    try {
      await client.post("/admin/generate-drafts");
      toast.success("Generazione bozze avviata: gli articoli appariranno tra poco come bozze. Aggiorna la lista.");
      setTimeout(loadArticles, 8000);
    } catch { toast.error("Avvio generazione non riuscito."); }
    finally { setGenLoading(false); }
  };
  const toggleAutoCat = async (key) => {
    const next = autoCats.includes(key) ? autoCats.filter((c) => c !== key) : [...autoCats, key];
    setAutoCats(next);
    await client.put("/admin/settings", { categories: next }).catch(() => {});
  };
  const saveRace = async (f) => {
    const payload = { ...f, orari: (f.orari_text || "").split("\n").map((s) => s.trim()).filter(Boolean) };
    delete payload.orari_text;
    if (f.id) { await client.put(`/admin/races/${f.id}`, payload); }
    else { await client.post("/admin/races", payload); }
    client.get("/races").then((r) => setRaces(r.data));
  };
  const loadSponsors = () => client.get("/sponsors").then((r) => setSponsors(r.data));
  const saveSponsor = async (f) => {
    const payload = { ...f, order: Number(f.order) || 0 };
    if (f.id) { await client.put(`/admin/sponsors/${f.id}`, payload); }
    else { await client.post("/admin/sponsors", payload); }
    loadSponsors();
  };
  const moveSponsor = async (idx, dir) => {
    const arr = [...sponsors];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    await Promise.all(arr.map((s, i) => (s.order !== i ? client.put(`/admin/sponsors/${s.id}`, { ...s, order: i }) : null)).filter(Boolean));
    loadSponsors();
    toast.success("Ordine aggiornato");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-10" data-testid="admin-panel">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display font-black uppercase text-3xl">Pannello Redazione</h1>
          <p className="text-sm text-slate-500">{admin.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <ChangePassword />
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 border border-slate-300 dark:border-slate-700 px-4 py-2 rounded-sm text-sm font-semibold hover:bg-legend-red hover:text-white transition-colors"><LogOut size={15} /> Esci</button>
        </div>
      </div>

      <LiveAdminPanel />

      <Tabs defaultValue="articoli">
        <TabsList className="flex flex-wrap h-auto">  <TabsTrigger value="articoli" data-testid="tab-articoli"><Newspaper size={15} className="mr-1.5" /> Articoli</TabsTrigger>
          <TabsTrigger value="gare" data-testid="tab-gare"><Flag size={15} className="mr-1.5" /> Gare</TabsTrigger>
          <TabsTrigger value="sponsor" data-testid="tab-sponsor"><Megaphone size={15} className="mr-1.5" /> Pubblicità</TabsTrigger>
          <TabsTrigger value="contatti" data-testid="tab-contatti"><Mail size={15} className="mr-1.5" /> Contatti</TabsTrigger>
          <TabsTrigger value="proposte" data-testid="tab-proposte"><Inbox size={15} className="mr-1.5" /> Proposte eventi{eventSubs.length > 0 && <span className="ml-1.5 text-[10px] bg-legend-red text-white px-1.5 py-0.5 rounded-sm">{eventSubs.length}</span>}</TabsTrigger>
          <TabsTrigger value="abbonati" data-testid="tab-abbonati"><CreditCard size={15} className="mr-1.5" /> Abbonati</TabsTrigger>
        </TabsList>

        <TabsContent value="articoli" className="mt-5">
          <ArticlesPanel
            articles={articles} articleFields={articleFields} saveArticle={saveArticle}
            togglePublish={togglePublish} postToIg={postToIg} igKitManual={igKitManual}
            generateDrafts={generateDrafts} genLoading={genLoading} autoCats={autoCats}
            toggleAutoCat={toggleAutoCat} AUTO_OPTIONS={AUTO_OPTIONS} del={del} setArticles={setArticles}
          />
        </TabsContent>

        <TabsContent value="gare" className="mt-5">
          <RacesPanel races={races} raceFields={raceFields} saveRace={saveRace} del={del} setRaces={setRaces} />
        </TabsContent>

        <TabsContent value="sponsor" className="mt-5">
          <SponsorsPanel sponsors={sponsors} sponsorFields={sponsorFields} saveSponsor={saveSponsor} moveSponsor={moveSponsor} del={del} setSponsors={setSponsors} />
        </TabsContent>

        {/* Contatti */}
        <TabsContent value="contatti" className="mt-5">
          <div className="space-y-2">
            {contacts.length === 0 && <p className="text-slate-500 text-sm">Nessuna richiesta ricevuta.</p>}
            {contacts.map((c) => (
              <div key={c.id} className="p-4 rounded-sm border border-slate-200 dark:border-slate-800 bg-white dark:bg-legend-paper">
                <div className="flex items-center justify-between"><p className="font-semibold">{c.name} <span className="text-xs font-mono text-legend-red">· {c.kind === "sponsor" ? "Pubblicità" : c.kind}</span></p><span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString("it-IT")}</span></div>
                <p className="text-sm text-slate-500">{c.email}</p>
                <p className="text-sm mt-2">{c.message}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Proposte eventi */}
        <TabsContent value="proposte" className="mt-5">
          <p className="text-xs text-slate-500 mb-4">Eventi e locandine inviati da organizzatori e appassionati dal calendario pubblico. Verificali e, se validi, aggiungili dalla scheda <strong>Gare</strong>.</p>
          <div className="space-y-2">
            {eventSubs.length === 0 && <p className="text-slate-500 text-sm" data-testid="no-event-submissions">Nessuna proposta ricevuta.</p>}
            {eventSubs.map((s) => (
              <div key={s.id} className="p-4 rounded-sm border border-slate-200 dark:border-slate-800 bg-white dark:bg-legend-paper" data-testid={`event-submission-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-display font-bold uppercase text-sm">{s.title} <span className="text-[10px] font-mono bg-slate-100 dark:bg-white/10 text-slate-500 px-1.5 py-0.5 rounded-sm">{s.discipline}</span></p>
                    <p className="text-xs text-slate-500 mt-0.5">{s.organizer} · <a href={`mailto:${s.email}`} className="text-legend-red underline">{s.email}</a></p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-slate-400">{new Date(s.created_at).toLocaleDateString("it-IT")}</span>
                    <IconBtn data-testid={`delete-event-submission-${s.id}`} title="Elimina proposta" onClick={() => del("/admin/event-submissions", s.id, setEventSubs, eventSubs)}><Trash2 size={15} /></IconBtn>
                  </div>
                </div>
                <div className="mt-2 text-sm text-slate-600 dark:text-slate-300 space-y-0.5">
                  {s.date && <p>📅 {s.date}</p>}
                  {s.location && <p>📍 {s.location}</p>}
                  {s.link && /^https?:\/\//i.test(s.link) && <p>🔗 <a href={s.link} target="_blank" rel="noreferrer" className="text-legend-red underline break-all">{s.link}</a></p>}
                  {s.description && <p className="mt-1 whitespace-pre-wrap">{s.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Abbonati */}
        <TabsContent value="abbonati" className="mt-5">
          <div className="space-y-2">
            {subs.length === 0 && <p className="text-slate-500 text-sm">Nessun abbonamento registrato.</p>}
            {subs.map((s, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-sm border border-slate-200 dark:border-slate-800 bg-white dark:bg-legend-paper">
                <div><p className="font-semibold text-sm">{s.email || "—"}</p><p className="text-xs text-slate-500 font-mono">{s.lookup_key} · {s.amount}€</p></div>
                <span className={`text-xs font-mono uppercase px-2 py-1 rounded-sm ${s.payment_status === "paid" ? "bg-emerald-500 text-white" : "bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-slate-300"}`}>{s.payment_status}</span>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
